import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ClipboardText,
  SignOut,
  DotsThreeVertical,
  Trash,
  Phone,
  MapPin,
  ArrowsClockwise,
  WhatsappLogo,
} from "@phosphor-icons/react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const STATUS_STYLES = {
  new: "bg-[var(--gold)]/20 text-[var(--navy)] border-[var(--gold)]",
  contacted: "bg-blue-100 text-blue-800 border-blue-300",
  completed: "bg-green-100 text-green-800 border-green-300",
  cancelled: "bg-slate-200 text-slate-600 border-slate-300",
};
const STATUSES = ["new", "contacted", "completed", "cancelled"];

function StatCard({ label, value, accent }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`stat-${label.toLowerCase()}`}>
      <p className="text-xs uppercase tracking-wider font-bold text-slate-400">
        {label}
      </p>
      <p
        className="font-display text-4xl mt-2"
        style={{ color: accent || "var(--navy)" }}
      >
        {value}
      </p>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, contacted: 0, completed: 0 });
  const [filter, setFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [b, s] = await Promise.all([
        api.get("/admin/bookings", { params: filter === "all" ? {} : { status: filter } }),
        api.get("/admin/stats"),
      ]);
      setBookings(b.data);
      setStats(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
  }, [load]);

  const updateStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success(`Marked as ${status}`);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/bookings/${id}`);
      toast.success("Booking deleted");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const notifyOnWhatsApp = (b) => {
    // WhatsApp the CUSTOMER directly on their phone.
    const raw = (b.phone || "").replace(/\D/g, "");
    const to = raw.length === 10 ? `91${raw}` : raw;
    const text = `Hello ${b.name.split(" ")[0]}, this is Quickmaid Services. Thanks for your booking request for *${b.service}* on ${b.preferred_date}. When is a good time for our team to call you? — +91 9309451659`;
    const url = `https://wa.me/${to}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener");
  };

  const callCustomer = (b) => {
    const raw = (b.phone || "").replace(/\D/g, "");
    const to = raw.length === 10 ? `+91${raw}` : `+${raw}`;
    window.location.href = `tel:${to}`;
  };

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen flex bg-[var(--paper-2)]">
      {/* Sidebar */}
      <aside className="hidden md:flex flex-col w-64 bg-[var(--navy)] text-white p-6 fixed inset-y-0">
        <div className="flex items-center gap-2.5 mb-10">
          <span className="grid place-items-center h-9 w-9 rounded-full bg-[var(--gold)] text-[var(--navy)] font-display font-bold">
            Q
          </span>
          <span className="font-display text-lg leading-tight">
            Quick Maid<br />
            <span className="text-xs text-white/50 tracking-wider">ADMIN</span>
          </span>
        </div>
        <nav className="space-y-1">
          <div className="flex items-center gap-3 rounded-xl bg-white/10 px-4 py-3 font-semibold">
            <ClipboardText weight="fill" size={20} className="text-[var(--gold)]" />
            Bookings
          </div>
        </nav>
        <div className="mt-auto">
          <p className="text-xs text-white/50 mb-3 truncate">{user?.email}</p>
          <button
            onClick={doLogout}
            className="flex items-center gap-2 text-sm font-semibold text-white/80 hover:text-[var(--gold)] transition-colors"
            data-testid="logout-btn"
          >
            <SignOut size={18} /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 md:ml-64 p-5 md:p-10">
        <div className="flex items-center justify-between flex-wrap gap-4 mb-8">
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-[var(--navy)] tracking-tight">
              Booking Requests
            </h1>
            <p className="text-slate-500 mt-1">Manage and track all customer bookings.</p>
          </div>
          <div className="flex items-center gap-2 md:hidden">
            <button onClick={doLogout} className="text-sm font-semibold text-[var(--navy)]" data-testid="logout-btn-mobile">
              <SignOut size={20} />
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total" value={stats.total} />
          <StatCard label="New" value={stats.new} accent="var(--gold)" />
          <StatCard label="Contacted" value={stats.contacted} accent="#1d4ed8" />
          <StatCard label="Completed" value={stats.completed} accent="#15803d" />
        </div>

        <div className="flex items-center justify-between gap-3 mb-4">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44 bg-white" data-testid="status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {STATUSES.map((s) => (
                <SelectItem key={s} value={s} className="capitalize">
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load} className="gap-2" data-testid="refresh-btn">
            <ArrowsClockwise size={16} /> Refresh
          </Button>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="overflow-x-auto">
            <Table data-testid="bookings-table">
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Customer</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Preferred Date</TableHead>
                  <TableHead>Requested</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-16 text-slate-400">
                      Loading...
                    </TableCell>
                  </TableRow>
                ) : bookings.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-16 text-slate-400" data-testid="no-bookings">
                      No booking requests yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  bookings.map((b) => (
                    <TableRow key={b.id} data-testid={`booking-row-${b.id}`}>
                      <TableCell>
                        <p className="font-semibold text-[var(--navy)]">{b.name}</p>
                        <p className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                          <Phone size={12} /> {b.phone}
                        </p>
                        <p className="text-xs text-slate-400 flex items-start gap-1 mt-0.5 max-w-[220px]">
                          <MapPin size={12} className="mt-0.5 shrink-0" /> {b.address}
                        </p>
                        {b.message && (
                          <p className="text-xs text-slate-400 italic mt-1 max-w-[220px]">&ldquo;{b.message}&rdquo;</p>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{b.service}</TableCell>
                      <TableCell>{b.preferred_date}</TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {b.created_at ? format(new Date(b.created_at), "dd MMM, HH:mm") : "-"}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={`capitalize border ${STATUS_STYLES[b.status] || ""}`}
                          data-testid={`status-badge-${b.id}`}
                        >
                          {b.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => notifyOnWhatsApp(b)}
                            title="WhatsApp this customer"
                            data-testid={`whatsapp-notify-${b.id}`}
                          >
                            <WhatsappLogo size={20} weight="fill" className="text-[#25D366]" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => callCustomer(b)}
                            title="Call this customer"
                            data-testid={`call-customer-${b.id}`}
                          >
                            <Phone size={18} weight="fill" className="text-[var(--navy)]" />
                          </Button>
                          <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`actions-${b.id}`}>
                              <DotsThreeVertical size={22} weight="bold" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {STATUSES.map((s) => (
                              <DropdownMenuItem
                                key={s}
                                onClick={() => updateStatus(b.id, s)}
                                className="capitalize"
                                data-testid={`set-${s}-${b.id}`}
                              >
                                Mark as {s}
                              </DropdownMenuItem>
                            ))}
                            <DropdownMenuItem
                              onClick={() => remove(b.id)}
                              className="text-red-600 focus:text-red-600"
                              data-testid={`delete-${b.id}`}
                            >
                              <Trash size={16} className="mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      </main>
    </div>
  );
}
